import * as React from 'react';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { GoogleAdsLanguageGq } from '../../../graphql/projects/models/GoogleAdsLanguageGq';
import { GoogleAdsLocationGq } from '../../../graphql/projects/models/GoogleAdsLocationGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { GetSelectedKeywordsStatsGq } from '../../../graphql/projects/queries/useGetSelectedKeywordsStatsGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { useRankCapacityCheck } from '../../../logic/otherUtilities/useRankCapacityCheck';
import { ProjectTabChoices } from '../../../pages/ProjectPage/ProjectPage';
import { ProjectSettingsMenuChoices } from '../../../pages/ProjectSettingsPage/ProjectSettingsPage';
import { KeywordTableState } from '../../muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import VoxelAlert from '../Alert/VoxelAlert';
import VoxelLink from '../Link/VoxelLink';
import RankOption from './SelectRankOptionCard';
import VoxelDialog from './VoxelDialog';
import VoxelDialogActionCancel from './VoxelDialogActionCancel';
import VoxelDialogActionSubmit from './VoxelDialogActionSubmit';
import VoxelDialogActions from './VoxelDialogActions';
import VoxelDialogContent from './VoxelDialogContent';
import { KeywordFilteringGq } from '../../../graphql/projects/models/KeywordFilteringGq';
import { KeywordSelectingGq } from '../../../graphql/projects/models/KeywordSelectingGq';

export interface FormState {
  city: GoogleAdsLocationGq;
  country: GoogleAdsLocationGq;
  language: GoogleAdsLanguageGq;
}

export interface GetRankDataDialogProps {
  project: ProjectGq;
  selectedKeywordStats: GetSelectedKeywordsStatsGq;
  reload?: () => Promise<any>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  user: CurrentUserGq;
  filterState: KeywordFilteringGq;
  selectionState?: KeywordSelectingGq;
  resetSelection?: () => void;
  showProjectDomain?: () => void;
}

const GetRankDataDialog: React.FC<GetRankDataDialogProps> = ({
  project,
  selectedKeywordStats,
  filterState,
  reload,
  open,
  setOpen,
  user,
  selectionState,
  resetSelection,
  showProjectDomain,
}) => {
  const history = useHistory();

  const [selectNewRanks, setSelectNewRanks] = useState(true);
  const [selectUpdateRanks, setSelectUpdateRanks] = useState(
    !!(selectedKeywordStats?.selectedNewRanksCount === 0)
  );

  // Serp settings
  const serpSettingsExist = !!project?.serpLanguageId && !!project.serpLocationId;

  const closeDialog = () => {
    setOpen(false);
    setSelectNewRanks(true);
    setSelectUpdateRanks(!!(selectedKeywordStats?.selectedNewRanksCount === 0));
    history.push(`/${project.id}/${ProjectTabChoices.Manage}`);
  };

  const [loading, setLoading] = useState(false);
  const collectSerpDataMutation = projectsApiService.mutations.serp.useCollectSerpData();
  const handleCollectSerpData = useCallback(() => {
    setLoading(true);
    showProjectDomain && showProjectDomain();
    collectSerpDataMutation({
      projectId: filterState.projectId,
      keywordQuery: {
        filtering: filterState,
        selecting: {
          ...selectionState,
          emptyIsAll: true,
        },
      },
      newRanksSelected: selectNewRanks,
      updateRanksSelected: selectUpdateRanks,
    })
      .then(() => reload())
      .then(() => resetSelection && resetSelection())
      .then(() => setLoading(false))
      .finally(closeDialog);
  }, [selectionState, filterState, selectNewRanks, selectUpdateRanks, reload]);

  const rankCapacity = useRankCapacityCheck(
    user.team,
    selectedKeywordStats.selectedNewRanksCount + selectedKeywordStats.selectedUpdateRanksCount
  );

  return (
    <>
      {open && (
        <VoxelDialog
          title="Add rank data"
          open={open}
          onClose={closeDialog}
          minWClass={'min-w-[600px]'}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <VoxelDialogContent className={`flex flex-col gap-6 flex-nowrap `}>
            <div className="flex flex-col gap-y-6 mt-10">
              <RankOption
                title={'Get new rank data'}
                selectedOption={selectNewRanks}
                setSelectedOption={setSelectNewRanks}
                optionKeywordCount={selectedKeywordStats?.selectedNewRanksCount}
                disabled={!(selectedKeywordStats?.selectedNewRanksCount > 0)}
              />
              <RankOption
                title={'Update existing rank data'}
                selectedOption={selectUpdateRanks}
                setSelectedOption={setSelectUpdateRanks}
                optionKeywordCount={selectedKeywordStats?.selectedUpdateRanksCount}
                dateList={selectedKeywordStats?.selectedUpdateRanksDates}
                disabled={!(selectedKeywordStats?.selectedUpdateRanksCount > 0)}
              />
            </div>
          </VoxelDialogContent>
          <VoxelDialogActions>
            <VoxelDialogActionCancel onClick={closeDialog} disabled={loading} />
            <VoxelDialogActionSubmit
              title={'Confirm'}
              onClick={handleCollectSerpData}
              disabled={
                !rankCapacity?.rankSelectionOk ||
                loading ||
                !(selectNewRanks || selectUpdateRanks) ||
                !serpSettingsExist ||
                (!(selectedKeywordStats?.selectedUpdateRanksCount > 0) &&
                  !(selectedKeywordStats?.selectedNewRanksCount > 0))
              }
            />
          </VoxelDialogActions>
          {project.progressHasContentPlan && selectUpdateRanks && (
            <VoxelAlert
              severity="warning"
              size="sm"
              iconSize="lg"
              roundedClass="rounded-none last:rounded-b-xl"
            >
              Updated keywords will be removed from content plan. You will have to re-add the
              keywords to a plan once the ranks has been updated.
            </VoxelAlert>
          )}
          {!rankCapacity?.rankSelectionOk && (
            <VoxelAlert
              severity="error"
              size="sm"
              iconSize="lg"
              roundedClass="rounded-none last:rounded-b-xl"
            >
              Not enough left of your teams capacity. Go to{' '}
              <VoxelLink href="/team/usage" openInNewTab openInNewIcon>
                App usage
              </VoxelLink>{' '}
              for more information
            </VoxelAlert>
          )}
          {!serpSettingsExist && (
            <VoxelAlert
              severity="error"
              size="sm"
              iconSize="lg"
              roundedClass="rounded-none last:rounded-b-xl"
            >
              This project is missing rank location or language settings.
              <br></br>
              Visit the{' '}
              <VoxelLink
                openInNewTab
                openInNewIcon
                href={
                  history.location.pathname.split('/manage', 1) +
                  '/' +
                  ProjectTabChoices.Settings +
                  '/' +
                  ProjectSettingsMenuChoices.DataCollectionSettings
                }
              >
                data settings page
              </VoxelLink>{' '}
              and configure them before continuing.
            </VoxelAlert>
          )}
        </VoxelDialog>
      )}
    </>
  );
};

export default GetRankDataDialog;
